import moment from 'moment-timezone';

import { formatRelativeTime } from '@/helpers/date';
import { formatPhone } from '@/helpers/phone';
import i18n from '@/i18n';
import { getMoveWorkerActionItems } from '@/modules/companyNeed/helpers/move';
import { RangeType, UnavailabilityType } from '@/modules/companyNeed/types';

import type { TableDropdownProps } from '@/components/tableDropdowns/types';
import type { AvatarProps } from '@/dsComponents/Avatar/types';
import type {
	DropdownCollection,
	TableAction,
	TableCellProps,
} from '@/dsComponents/Table/types';
import type { Unavailability } from '@/modules/companyNeed/types';
import type {
	MoveWorker,
	MoveWorkerCompanyNeedCommands,
} from '@/modules/companyNeed/types/moveWorker';
import type {
	MatchingProfileCellProps,
	MatchingProfileFormatterProps,
	ProfileFormatterProps,
} from '@/modules/companyNeed/views/details/matching/types';
import type { Timestamp } from '@/types/common';

import { AvatarColor, AvatarSize } from '@/dsComponents/Avatar/types';
import { DropdownGeneratorActionType } from '@/dsComponents/Dropdown/types';

const DEFAULT_DEBOUNCE = 200;

export const mapProfileFormatterPropsToMoveWorker = ({
	id,
	firstname,
	lastname,
	email,
	phone,
	picture,
	fromJobOffer,
	history,
	availableCommands,
}: ProfileFormatterProps): MoveWorker => ({
	id,
	firstname,
	lastname,
	email,
	phone,
	picture,
	fromJobOffer,
	history,
	availableCommands,
});

const isMatchingProfileRow = (
	profile: ProfileFormatterProps
): profile is MatchingProfileFormatterProps =>
	typeof profile.inPool === 'boolean' &&
	typeof profile.isSuggested === 'boolean';

const buildAvatarProps = (profile: ProfileFormatterProps): AvatarProps => ({
	firstname: profile.firstname,
	lastname: profile.lastname,
	size: AvatarSize.size24,
	imageUrl: profile.picture || undefined,
	displayName: true,
	fontWeight: 500,
	fontSize: '13px',
	bottomStatus: profile.registered
		? { icon: 'mobile', color: AvatarColor.blue }
		: undefined,
});

export function profileFormatter(
	profile: MatchingProfileFormatterProps
): MatchingProfileCellProps;
export function profileFormatter(profile: ProfileFormatterProps): AvatarProps;
export function profileFormatter(
	profile: ProfileFormatterProps
): AvatarProps | MatchingProfileCellProps {
	const avatarProps = buildAvatarProps(profile);
	if (!isMatchingProfileRow(profile)) {
		return avatarProps;
	}
	return {
		avatarProps,
		isSuggested: profile.isSuggested,
		inPool: profile.inPool,
	};
}

export const getUnavailabilityTypeIcon = (
	unavailabilityType: UnavailabilityType,
	rangeType: RangeType
): string => {
	switch (unavailabilityType) {
		case UnavailabilityType.MISSION:
			return 'cal-exclamation';
		case UnavailabilityType.DEFAULT:
			return rangeType === RangeType.FROM
				? 'circle-half-dashed'
				: 'circle-full-dashed';
	}
};

export const unavailabilityFormatter = (
	unavailability: Unavailability
): TableCellProps => {
	return {
		cellTitle: i18n.global.t(
			`page.companyNeed.details.table.unavailability.${unavailability.unvailabilityType}.${unavailability.rangeType}`,
			{
				date: moment(unavailability.date).format('DD MMM'),
			}
		),
		icon: {
			iconType: getUnavailabilityTypeIcon(
				unavailability.unvailabilityType,
				unavailability.rangeType
			),
			iconColor: 'grey',
		},
	};
};

export const lastUpdateFormatter = (
	lastUpdate: Timestamp,
	from: 'app' | 'website' = 'app'
): TableDropdownProps => {
	const dropdownIcon = from === 'app' ? 'device-mobile' : 'device-desktop';
	const date = formatRelativeTime(lastUpdate, {
		style: 'short',
		includeGranular: false,
	});
	const dropdownText = i18n.global.t(
		'page.companyNeed.details.table.lastUpdate.dropdown.defaultText',
		{
			date,
		}
	);

	return {
		mode: 'hover',
		debounce: DEFAULT_DEBOUNCE,
		trigger: date,
		items: [
			{
				text: i18n.global.t(
					'page.companyNeed.details.table.lastUpdate.dropdown.title'
				),
				textStyle: { color: 'grey' },
			},
			{ text: dropdownText, icon: dropdownIcon },
		],
	};
};

export const workerPhoneFormatter = (phone: string): TableCellProps => {
	return {
		cellTitle: formatPhone(phone),
	};
};

export const distanceFormatter = (distance: number): TableCellProps => {
	if (distance === null || distance === undefined || distance === 0) {
		return {
			cellTitle: i18n.global.t('global.notProvided'),
			fontType: 'passive',
		};
	}

	return {
		cellTitle: `${Math.round(distance)} km`,
	};
};

export const missionsWithClientFormatter = (
	missionsWithClient: number
): TableCellProps => {
	return {
		cellTitle: missionsWithClient.toString(),
	};
};

export const rejectionDateFormatter = (rejectionDate: Date): TableCellProps => {
	return {
		cellTitle: moment(rejectionDate).locale('fr').format('D MMM, YY'),
		fontType: 'passive',
	};
};

interface FormatActionsOptions {
	includeRefuse?: boolean;
	companyId?: string;
	isFromMatching?: boolean;
	fromJobOffer?: boolean;
	onViewDetails?: (worker: MoveWorker) => void;
	onRemoveFromPool?: (workerId: string) => void;
	onMoveSuccess?: (
		command?: MoveWorkerCompanyNeedCommands
	) => void | Promise<void>;
}

export const formatActions = (
	worker: ProfileFormatterProps,
	companyNeedId: string,
	options: FormatActionsOptions = {}
): TableAction[] => {
	const actionsItem: DropdownCollection[] = getMoveWorkerActionItems(
		mapProfileFormatterPropsToMoveWorker(worker),
		companyNeedId,
		{
			...options,
			isFromMatching: options.isFromMatching ?? false,
		}
	).map((action) => ({
		type: DropdownGeneratorActionType.item,
		item: {
			iconLeft: action.iconLeft,
			iconLeftColor: action.iconLeftColor,
			label: action.label,
			labelColor: action.labelColor,
			onClick: action.onClick,
		},
	}));

	return [
		{
			button: { iconOnly: 'dots', type: 'neutral', variant: 'ghost' },
			dropdownItems: () => actionsItem,
			width: '240px',
		},
	];
};
